import {Logger} from '@discordapp/common/Logger';
import {Storage} from '@discordapp/storage';

const logger = new Logger('useStateFromStores');

let executionWindowThresholdMs = getUseStateFromStoresExecutionWindowThresholdMs();

export function getUseStateFromStoresExecutionWindowThresholdMs(defaultMs: number = 60000) {
  if (process.env.NODE_ENV !== 'development') {
    return defaultMs;
  }
  return Storage.get('useStateFromStoresExecutionWindowThresholdMs', defaultMs) ?? defaultMs;
}

export function setUseStateFromStoresExecutionWindowThresholdMs(ms: number) {
  executionWindowThresholdMs = ms;
  Storage.set('useStateFromStoresExecutionWindowThresholdMs', ms);

  clearInterval(enabledIntervalId);
  if (enabled) {
    enabledIntervalId = setInterval(flushViolators, executionWindowThresholdMs);
  }
}

let executionTimeWarningThresholdMs = getUseStateFromStoresExecutionTimeWarningThresholdMs();

export function getUseStateFromStoresExecutionTimeWarningThresholdMs(defaultMs: number = 10) {
  if (process.env.NODE_ENV !== 'development') {
    return defaultMs;
  }
  return Storage.get('useStateFromStoresExecutionTimeWarningThresholdMs', defaultMs) ?? defaultMs;
}

export function setUseStateFromStoresExecutionTimeWarningThresholdMs(ms: number) {
  executionTimeWarningThresholdMs = ms;
  Storage.set('useStateFromStoresExecutionTimeWarningThresholdMs', ms);
}

let executionCountWarningThreshold = getUseStateFromStoresExecutionCountWarningThreshold();

export function getUseStateFromStoresExecutionCountWarningThreshold(defaultThreshold: number = 1000) {
  if (process.env.NODE_ENV !== 'development') {
    return defaultThreshold;
  }
  return Storage.get('useStateFromStoresExecutionCountWarningThreshold', defaultThreshold) ?? defaultThreshold;
}

export function setUseStateFromStoresExecutionCountWarningThreshold(threshold: number) {
  executionCountWarningThreshold = threshold;
  Storage.set('useStateFromStoresExecutionCountWarningThreshold', threshold);
}

let specificHookFilter: string = getUseStateFromStoresSpecificHookFilter();

export function setUseStateFromStoresSpecificHookFilter(filter: string) {
  specificHookFilter = filter;
  Storage.set('useStateFromStoresSpecificHookFilter', filter);
}

export function getUseStateFromStoresSpecificHookFilter(defaultFilter: string = '') {
  if (process.env.NODE_ENV !== 'development') {
    return defaultFilter;
  }
  return Storage.get('useStateFromStoresSpecificHookFilter', defaultFilter) ?? defaultFilter;
}

let enabled = getUseStateFromStoresDebuggingEnabled();
let enabledIntervalId: NodeJS.Timeout | undefined = undefined;
if (enabled) {
  enabledIntervalId = setInterval(flushViolators, executionWindowThresholdMs);
}

export function getUseStateFromStoresDebuggingEnabled(defaultEnabled: boolean = false): boolean {
  if (process.env.NODE_ENV !== 'development') {
    return defaultEnabled;
  }

  return Storage.get('useStateFromStoresDebuggingEnabled', defaultEnabled) ?? defaultEnabled;
}

export function setUseStateFromStoresDebuggingEnabled(value: boolean) {
  enabled = value;
  Storage.set('useStateFromStoresDebuggingEnabled', value);

  if (enabled) {
    enabledIntervalId = setInterval(flushViolators, executionWindowThresholdMs);
  } else {
    clearInterval(enabledIntervalId);
    enabledIntervalId = undefined;
  }
}

const useStateFromStoresHookInfo = new Map<string, UseStateFromStoresHookInfo>();

export interface UseStateFromStoresHookInfo {
  name: string;
  execCount: number;
  execTime: number;
  warned: boolean;
}

export function getUseStateFromStoresHookInfo() {
  if (!enabled) {
    return undefined;
  }

  const hookCaller = getUseStateFromStoresFunctionCaller();
  const hookInfo = useStateFromStoresHookInfo.get(hookCaller) ?? {
    name: hookCaller,
    execCount: 0,
    execTime: 0,
    warned: false,
  };
  useStateFromStoresHookInfo.set(hookCaller, hookInfo);
  return hookInfo;
}

export function trackGetStateFromStoresPerformance<State>(
  hookInfo: UseStateFromStoresHookInfo | undefined,
  getStateFromStores: () => State,
) {
  if (!enabled || hookInfo == null) {
    return getStateFromStores();
  }

  const start = performance.now();
  const newState = getStateFromStores();
  const end = performance.now();
  hookInfo.execTime += end - start;
  hookInfo.execCount++;

  if (hookInfo.warned === false && hasExceededThreshold(hookInfo)) {
    hookInfo.warned = true;
    const {name, execTime, execCount} = hookInfo;
    logger.log(`${name} cumulatively used ${execTime}ms of execution time and ran ${execCount} times.`);
  }

  return newState;
}

function getUseStateFromStoresFunctionCaller(): string {
  const stack = new Error().stack?.split('\n');
  const stackOrDefault = stack != null ? stack : [];
  for (let i = 3; i < stackOrDefault.length; i++) {
    const frame = stackOrDefault[i].trim();
    const functionName = frame.split(' ')[1];
    if (
      functionName === 'useStateFromStores'
      || functionName === 'useStateFromStoresArray'
      || functionName === 'useStateFromStoresObject'
    ) {
      continue;
    }

    return functionName;
  }

  return 'unknown';
}

function hasExceededThreshold(hookInfo: UseStateFromStoresHookInfo) {
  if (hookInfo.name === 'anonymous') {
    return false;
  }

  if (specificHookFilter !== '' && hookInfo.name !== specificHookFilter) {
    return false;
  }

  return hookInfo.execCount > executionCountWarningThreshold || hookInfo.execTime > executionTimeWarningThresholdMs;
}

function hasViolator() {
  for (const [_, hookInfo] of useStateFromStoresHookInfo) {
    if (!hasExceededThreshold(hookInfo)) {
      continue;
    }
    return true;
  }
  return false;
}

function flushViolators() {
  if (hasViolator() || specificHookFilter !== '') {
    printViolators();
  } else {
    logger.log('No violators found');
  }

  for (const [_, hookInfo] of useStateFromStoresHookInfo) {
    hookInfo.warned = false;
    hookInfo.execTime = 0;
    hookInfo.execCount = 0;
  }
}

const printViolators = (
  col1Width: number = 34,
  col2Width: number = 20,
  col3Width: number = 20,
  colTotalWidth: number = col1Width + col2Width + col3Width + 6,
) => {
  logger.log('|'.padEnd(colTotalWidth, '-') + '|');
  logger.log('| Consumers of `useStateFromStores` exceeding warning thresholds:'.padEnd(colTotalWidth, ' ') + '|');
  logger.log('|'.padEnd(colTotalWidth, '-') + '|');
  logger.log(
    `| ${'Function/Component Name'.padEnd(col1Width, ' ')}`
      + `| ${'Total Exec Time'.padEnd(col2Width, ' ')}`
      + `| ${'Total Exec Count'.padEnd(col3Width, ' ')}|`,
  );
  logger.log('|'.padEnd(colTotalWidth, '-') + '|');
  for (const [key, hookInfo] of useStateFromStoresHookInfo) {
    const isSpecificHook = specificHookFilter !== '' && key === specificHookFilter;
    if (!isSpecificHook && !hasExceededThreshold(hookInfo)) {
      continue;
    }
    logger.log(
      `| ${key.padEnd(col1Width, ' ')}`
        + `| ${(hookInfo.execTime.toFixed(2) + 'ms').padEnd(col2Width, ' ')}`
        + `| ${hookInfo.execCount.toString().padEnd(col3Width, ' ')}|`,
    );
  }
  logger.log('|'.padEnd(colTotalWidth, '-') + '|');
};
